import { MONEY_AMOUNTS, DISPLAY_ONLY } from '../constants/financialPrecision';

/**
 * Smart decimal formatter for financial inputs
 * - Supports configurable precision per category
 * - 0-N decimals: Shows minimum decimals (e.g., 1000.50)
 * - N+ decimals: Shows all significant decimals, removes trailing zeros (e.g., 1000.123456)
 * - No thousand separators for cleaner input (use displayFormatter for commas)
 *
 * @param {number|string} value - Value to format
 * @param {object} config - Precision category configuration (default: MONEY_AMOUNTS)
 *
 * Examples (with MONEY_AMOUNTS):
 *   1000.5 → "1000.50" (normalized to 2)
 *   1000.50 → "1000.50" (kept as is)
 *   1234.123456789012 → "1234.123456789012" (all decimals shown)
 *   1000.12340000 → "1000.1234" (trailing zeros removed, 4 decimals)
 *   1000 → "1000.00" (normalized to 2)
 */
export const smartDecimalFormatter = (value, config = MONEY_AMOUNTS) => {
  if (!value && value !== 0) return '';

  // If value is already a formatted string with commas, parse it first
  const cleanValue = typeof value === 'string' ? value.replace(/,/g, '') : value;
  const numValue = typeof cleanValue === 'string' ? parseFloat(cleanValue) : cleanValue;
  if (isNaN(numValue)) return '';

  const maxDecimals = config.displayMaxDecimals;
  const minDecimals = config.displayMinDecimals;

  // Scale-aware rounding. JS doubles carry ~15 significant digits, so a
  // hard-coded `displayMaxDecimals: 12` is undeliverable when the integer
  // part already eats most of the precision budget — toFixed(12) and even
  // Math.round(v * 1e12) / 1e12 surface IEEE-754 binary artifacts.
  // Example: (95405.2).toFixed(12) === "95405.199999999997" even though
  // 95405.2 and 95405.199999999997 are the SAME double (Object.is true).
  // Cap effective decimals at what the magnitude can safely carry so
  // 95405.20 stays "95,405.20" instead of becoming "95,405.199999999997".
  const absValue = Math.abs(numValue);
  const integerDigits = absValue >= 1 ? Math.floor(Math.log10(absValue)) + 1 : 1;
  const safeMaxDecimals = Math.max(
    minDecimals,
    Math.min(maxDecimals, 15 - integerDigits)
  );
  const displayMultiplier = Math.pow(10, safeMaxDecimals);
  const rounded = Math.round(numValue * displayMultiplier) / displayMultiplier;

  // Prevent -0.00 by checking if rounded value is 0
  if (rounded === 0) {
    return '0.' + '0'.repeat(minDecimals || 2);
  }

  // Use the shortest round-trippable decimal representation. Scientific
  // notation (very rare for typical money values, possible for tiny
  // numbers) falls back to toFixed so the cleanup regex still has a shot.
  let formatted = String(rounded);
  if (formatted.includes('e') || formatted.includes('E')) {
    formatted = rounded.toFixed(safeMaxDecimals);
  }

  let parts = formatted.split('.');
  let integer = parts[0];
  let decimal = parts[1] || '';

  // Defensive scrub of residual artifacts in the decimal part only.
  // The scale-aware rounding above should make these no-ops, but they
  // remain a safety net for the toFixed fallback path.
  if (decimal) {
    decimal = decimal
      .replace(/0{6,}\d{1,2}$/, '')   // "...0000001" tail
      .replace(/9{6,}\d{0,2}$/, '')   // "...9999997" tail
      .replace(/0+$/, '');            // trailing zeros (only on decimal part)
  }

  const integerWithCommas = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  if (decimal.length === 0) {
    return `${integerWithCommas}.${'0'.repeat(minDecimals)}`;
  } else if (decimal.length < minDecimals) {
    return `${integerWithCommas}.${decimal}${'0'.repeat(minDecimals - decimal.length)}`;
  } else {
    return `${integerWithCommas}.${decimal}`;
  }
};

/**
 * Parser to remove formatting (commas, dollar signs)
 * Handles: "$1,234.56" → "1234.56"
 */
export const smartDecimalParser = (value) => {
  if (!value) return '';
  return value.replace(/\$\s?|(,*)/g, '');
};

/**
 * Get step value for a precision category
 * @param {object} config - Precision category configuration (default: MONEY_AMOUNTS)
 * @returns {number} Step value for InputNumber component
 *
 * Examples:
 *   getSmartDecimalStep(MONEY_AMOUNTS)  // 0.000000000001
 *   getSmartDecimalStep(PERCENTAGES)    // 0.01
 */
export const getSmartDecimalStep = (config = MONEY_AMOUNTS) => {
  return config.minStep;
};

/**
 * Display-only formatter with thousand separators (for read-only displays, not inputs)
 * Use this for: tables, reports, summaries, view-mode displays
 *
 * @param {number|string} value - Value to format
 * @param {object} config - Precision category configuration (default: DISPLAY_ONLY)
 *
 * Examples:
 *   displayFormatter(1234567.89, DISPLAY_ONLY) → "1,234,567.89"
 *   displayFormatter(1000.5, MONEY_AMOUNTS) → "1,000.50"
 */
export const displayFormatter = (value, config = DISPLAY_ONLY) => {
  // Use the standard formatter logic (no commas)
  const formatted = smartDecimalFormatter(value, config);

  // Add thousand separators for display
  const parts = formatted.split('.');
  const integer = parts[0];
  const decimal = parts[1];

  const integerWithCommas = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return decimal ? `${integerWithCommas}.${decimal}` : integerWithCommas;
};

// Re-export helper functions from financialPrecision for convenience
export { roundToPrecision, isBalanced } from '../constants/financialPrecision';
